import {useState, useRef, useCallback, useMemo, useEffect, type ReactNode, type RefObject} from 'react'
import {clsx} from 'clsx'
import {ActionList, ActionMenu, Banner, DragHandle, IconButton, Spinner, TreeView} from '@primer/react'
import {FileDirectoryIcon, FoldIcon, GearIcon, UnfoldIcon} from '@primer/octicons-react'
import {DiffFileHeader} from '@github-ui/diff-file-header'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {StaticSplitDiffPreview} from './StaticSplitDiffPreview'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {StaticDiffLine} from '@github-ui/diff-lines/conversation-types'
import {DiffStats} from '@github-ui/diffs/DiffParts'
import {useDiffSource} from '../../hooks/use-diff-source'
import {announce} from '@github-ui/aria-live'
import {parseUnifiedDiff} from '../../utils/parse-unified-diff'
import {DiffFileTree} from '@github-ui/diff-file-tree/file-tree'
import type {FileProps} from '@github-ui/diff-file-tree/file-tree'
import type {DiffDelta} from '@github-ui/diff-file-tree/diff-file-tree-helpers'
import {FileStatusIcon} from '@github-ui/diff-file-tree/file-status-icon'
import type {DiffData} from '../../types/diff'
import styles from './DiffWorkbenchContent.module.css'

const FILE_TREE_DEFAULT_WIDTH = 220
const FILE_TREE_MIN_WIDTH = 160
const FILE_TREE_MAX_WIDTH = 400
const FILE_TREE_WIDTH_PROPERTY = '--file-tree-width'

function clampFileTreeWidth(width: number) {
  return Math.min(Math.max(width, FILE_TREE_MIN_WIDTH), FILE_TREE_MAX_WIDTH)
}

type ViewFormat = 'unified' | 'split'
type FileTreePosition = 'left' | 'right'

const PATCH_STATUS = {
  ADDED: 'ADDED',
  DELETED: 'DELETED',
  RENAMED: 'RENAMED',
  COPIED: 'COPIED',
  MODIFIED: 'MODIFIED',
} as const

const SHORT_STATUS_TO_PATCH_STATUS: Record<string, string> = {
  A: PATCH_STATUS.ADDED,
  D: PATCH_STATUS.DELETED,
  R: PATCH_STATUS.RENAMED,
  C: PATCH_STATUS.COPIED,
  M: PATCH_STATUS.MODIFIED,
}

function mapStatus(status: string): string {
  return SHORT_STATUS_TO_PATCH_STATUS[status] ?? PATCH_STATUS.MODIFIED
}

function toDiffDeltas(files: DiffData['files']): DiffDelta[] {
  return files.map(file => ({
    path: file.path,
    pathDigest: file.path_digest,
    changeType: mapStatus(file.status),
  }))
}

export interface DiffWorkbenchContentProps {
  repoNwo: string
  headRef: string
  initialFilePath?: string | null
}

/**
 * The changeset-backed multi-file diff view (file tree + per-file diff body + Unified/Split/
 * Wrap-lines + collapse-all), as a host-agnostic content component. Unlike the former
 * `DiffWorkbench`, this has no `isOpen`/`onClose`/`withinDialog` props, no close button, and no
 * escape-key/focus-trap handling of its own — whatever hosts it (e.g. a workbench tab) owns that.
 */
export function DiffWorkbenchContent({initialFilePath}: DiffWorkbenchContentProps) {
  const [showFileTree, setShowFileTree] = useState(true)
  const [viewFormat, setViewFormat] = useState<ViewFormat>('unified')
  const [wrapLines, setWrapLines] = useState(true)
  const [hideWhitespace, setHideWhitespace] = useState(false)
  const [fileTreePosition, setFileTreePosition] = useState<FileTreePosition>('left')
  const [collapsedPaths, setCollapsedPaths] = useState<ReadonlySet<string>>(new Set())
  const [fileTreeWidth, setFileTreeWidth] = useState(FILE_TREE_DEFAULT_WIDTH)

  const {changeset, hasChangesetDiffs, changesetFilesReady, diffs, isLoading, contentUnavailable} = useDiffSource(
    true,
    {hideWhitespace},
  )
  // No REST fallback (changeset-only) — without this, a still-computing changeset would wrongly show the empty state.
  const changesetPending = changeset.available && !changesetFilesReady && !changeset.error
  const isWorkbenchLoading = hasChangesetDiffs ? isLoading : changesetPending

  useEffect(() => {
    if (changeset.error) announce(changeset.error)
  }, [changeset.error])

  const fileTreePanelRef = useRef<HTMLDivElement>(null)
  const fileTreeHandleRef = useRef<HTMLDivElement>(null)
  const dragStartClientXRef = useRef(0)
  const dragStartWidthRef = useRef(0)
  const currentFileTreeWidthRef = useRef(fileTreeWidth)

  const applyFileTreeWidth = useCallback((width: number) => {
    const next = clampFileTreeWidth(width)
    currentFileTreeWidthRef.current = next
    fileTreePanelRef.current?.style.setProperty(FILE_TREE_WIDTH_PROPERTY, `${next}px`)
    const handle = fileTreeHandleRef.current
    if (handle) {
      handle.setAttribute('aria-valuenow', String(Math.round(next)))
      handle.setAttribute('aria-valuetext', `${Math.round(next)} pixels`)
    }
  }, [])

  const handleFileTreeResizeStart = useCallback((clientX: number) => {
    dragStartClientXRef.current = clientX
    dragStartWidthRef.current =
      fileTreePanelRef.current?.getBoundingClientRect().width ?? currentFileTreeWidthRef.current
  }, [])

  const handleFileTreeResize = useCallback(
    (value: number, isKeyboard: boolean) => {
      if (isKeyboard) {
        // DragHandle's keyboard step is a fixed +/-3 regardless of unit; scale it up for a
        // pixel-based width so arrow keys move the sidebar a noticeable amount.
        applyFileTreeWidth(currentFileTreeWidthRef.current + value * 10)
        return
      }
      // The handle sits on the file tree's diff-facing edge, so dragging toward the diff content
      // grows the tree when it's on the left, and shrinks it when it's on the right.
      const direction = fileTreePosition === 'left' ? 1 : -1
      const width = dragStartWidthRef.current + (value - dragStartClientXRef.current) * direction
      applyFileTreeWidth(width)
    },
    [applyFileTreeWidth, fileTreePosition],
  )

  const handleFileTreeResizeEnd = useCallback(() => {
    const width = Math.round(currentFileTreeWidthRef.current)
    applyFileTreeWidth(width)
    setFileTreeWidth(width)
  }, [applyFileTreeWidth])

  const handleFileTreeResizeReset = useCallback(() => {
    applyFileTreeWidth(FILE_TREE_DEFAULT_WIDTH)
    setFileTreeWidth(FILE_TREE_DEFAULT_WIDTH)
  }, [applyFileTreeWidth])

  const diffPanelRef = useRef<HTMLDivElement>(null)

  const scrollToFile = useCallback(
    (filePath: string) => {
      const panel = diffPanelRef.current
      if (!panel) return
      const element = panel.querySelector<HTMLElement>(`[data-file-path="${CSS.escape(filePath)}"]`)
      if (element) {
        // Leave the same gap above the file as the gap between files (see `.diffPanel`'s `gap`).
        const parsedGap = parseFloat(getComputedStyle(panel).rowGap)
        const fileGap = Number.isFinite(parsedGap) ? parsedGap : 0
        const top = Math.max(0, element.offsetTop - panel.offsetTop - fileGap)
        panel.scrollTo({top, behavior: 'smooth'})
      }
    },
    [diffPanelRef],
  )

  const handleFileSelect = useCallback(
    (filePath: string) => {
      setCollapsedPaths(prev => {
        if (!prev.has(filePath)) return prev
        const next = new Set(prev)
        next.delete(filePath)
        return next
      })
      scrollToFile(filePath)
    },
    [scrollToFile],
  )

  const diffDeltas = diffs ? toDiffDeltas(diffs.files) : []

  const fileNodeRenderer = useCallback(
    ({file}: FileProps) => {
      return (
        <TreeView.Item id={file.diff.path} onSelect={() => handleFileSelect(file.filePath)} key={file.diff.pathDigest}>
          <TreeView.LeadingVisual>
            <FileStatusIcon status={file.diff.changeType} />
          </TreeView.LeadingVisual>
          {file.fileName}
        </TreeView.Item>
      )
    },
    [handleFileSelect],
  )

  // Scroll to initial file once diffs are loaded
  useEffect(() => {
    if (initialFilePath && diffs && !isWorkbenchLoading) {
      scrollToFile(initialFilePath)
    }
  }, [initialFilePath, diffs, isWorkbenchLoading, scrollToFile])

  const toggleFileCollapsed = useCallback((path: string) => {
    setCollapsedPaths(prev => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }, [])

  const areAllFilesCollapsed = !!diffs && diffs.files.length > 0 && diffs.files.every(f => collapsedPaths.has(f.path))
  const toggleCollapseAllFiles = useCallback(() => {
    if (!diffs) return
    setCollapsedPaths(areAllFilesCollapsed ? new Set() : new Set(diffs.files.map(f => f.path)))
  }, [diffs, areAllFilesCollapsed])

  return (
    <div className={styles.root} role="region" aria-label="Changes">
      {diffs && diffs.files.length > 0 && (
        <div className={styles.summaryRow}>
          <div className={styles.summaryLeft}>
            <IconButton
              icon={FileDirectoryIcon}
              aria-label={showFileTree ? 'Collapse file tree' : 'Expand file tree'}
              aria-pressed={showFileTree}
              variant="invisible"
              size="small"
              onClick={() => setShowFileTree(!showFileTree)}
              className={styles.headerButton}
            />
            <DiffStats linesAdded={diffs.additions} linesDeleted={diffs.deletions} linesChanged={diffs.changes} />
          </div>
          <div className={styles.summaryRight}>
            <IconButton
              icon={areAllFilesCollapsed ? UnfoldIcon : FoldIcon}
              aria-label={areAllFilesCollapsed ? 'Expand all changed files' : 'Collapse all changed files'}
              variant="invisible"
              size="small"
              onClick={toggleCollapseAllFiles}
              className={styles.headerButton}
            />
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton
                  icon={GearIcon}
                  aria-label="Diff view options"
                  variant="invisible"
                  size="small"
                  className={styles.headerButton}
                />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay>
                <ActionList>
                  <ActionList.Group selectionVariant="single">
                    <ActionList.GroupHeading>View</ActionList.GroupHeading>
                    <ActionList.Item selected={viewFormat === 'unified'} onSelect={() => setViewFormat('unified')}>
                      Unified view
                    </ActionList.Item>
                    <ActionList.Item selected={viewFormat === 'split'} onSelect={() => setViewFormat('split')}>
                      Split view
                    </ActionList.Item>
                  </ActionList.Group>
                  <ActionList.Divider />
                  <ActionList.Group selectionVariant="multiple">
                    <ActionList.Item selected={hideWhitespace} onSelect={() => setHideWhitespace(v => !v)}>
                      Hide whitespace
                    </ActionList.Item>
                    <ActionList.Item selected={wrapLines} onSelect={() => setWrapLines(v => !v)}>
                      Wrap lines
                    </ActionList.Item>
                  </ActionList.Group>
                  <ActionList.Divider />
                  <ActionList.Group selectionVariant="single">
                    <ActionList.GroupHeading>File tree position</ActionList.GroupHeading>
                    <ActionList.Item
                      selected={fileTreePosition === 'left'}
                      onSelect={() => setFileTreePosition('left')}
                    >
                      Left
                    </ActionList.Item>
                    <ActionList.Item
                      selected={fileTreePosition === 'right'}
                      onSelect={() => setFileTreePosition('right')}
                    >
                      Right
                    </ActionList.Item>
                  </ActionList.Group>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </div>
        </div>
      )}
      <div className={styles.body}>
        {isWorkbenchLoading ? (
          <div className={styles.loading}>
            <Spinner size="medium" />
          </div>
        ) : !diffs || diffs.files.length === 0 ? (
          <div className={styles.emptyState}>{changeset.error ?? 'No changes found.'}</div>
        ) : (
          <>
            {showFileTree && fileTreePosition === 'left' && (
              <FileTreePanel
                position="left"
                diffDeltas={diffDeltas}
                fileNodeRenderer={fileNodeRenderer}
                width={fileTreeWidth}
                panelRef={fileTreePanelRef}
                handleRef={fileTreeHandleRef}
                onResizeStart={handleFileTreeResizeStart}
                onResize={handleFileTreeResize}
                onResizeEnd={handleFileTreeResizeEnd}
                onResizeReset={handleFileTreeResizeReset}
              />
            )}
            <div className={clsx(styles.diffPanel, !wrapLines && styles.noWrap)} ref={diffPanelRef}>
              {changeset.error && (
                <Banner
                  title="Changed file information needs refreshing"
                  description={changeset.error}
                  variant="warning"
                />
              )}
              {diffs.files.map(file => (
                <DiffFileEntry
                  key={file.path}
                  file={file}
                  contentUnavailable={contentUnavailable}
                  viewFormat={viewFormat}
                  isCollapsed={collapsedPaths.has(file.path)}
                  onToggleFileCollapsed={() => toggleFileCollapsed(file.path)}
                />
              ))}
            </div>
            {showFileTree && fileTreePosition === 'right' && (
              <FileTreePanel
                position="right"
                diffDeltas={diffDeltas}
                fileNodeRenderer={fileNodeRenderer}
                width={fileTreeWidth}
                panelRef={fileTreePanelRef}
                handleRef={fileTreeHandleRef}
                onResizeStart={handleFileTreeResizeStart}
                onResize={handleFileTreeResize}
                onResizeEnd={handleFileTreeResizeEnd}
                onResizeReset={handleFileTreeResizeReset}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

interface FileTreePanelProps {
  position: FileTreePosition
  diffDeltas: DiffDelta[]
  fileNodeRenderer: (props: FileProps) => ReactNode
  width: number
  panelRef: RefObject<HTMLDivElement | null>
  handleRef: RefObject<HTMLDivElement | null>
  onResizeStart: (clientX: number) => void
  onResize: (value: number, isKeyboard: boolean) => void
  onResizeEnd: () => void
  onResizeReset: () => void
}

function FileTreePanel({
  position,
  diffDeltas,
  fileNodeRenderer,
  width,
  panelRef,
  handleRef,
  onResizeStart,
  onResize,
  onResizeEnd,
  onResizeReset,
}: FileTreePanelProps) {
  return (
    <div
      ref={panelRef}
      className={clsx(styles.fileTreePanel, position === 'left' && styles.fileTreePanelLeft)}
      style={{[FILE_TREE_WIDTH_PROPERTY]: `${width}px`}}
    >
      <DiffFileTree diffs={diffDeltas} fileNodeRenderer={fileNodeRenderer} sortByDiffOrder />
      <div className={styles.fileTreeResizeHandle}>
        <DragHandle
          handleRef={handleRef as RefObject<HTMLDivElement>}
          dragTargetRef={panelRef}
          data-component="DiffWorkbenchContent.FileTreeResizeHandle"
          aria-label="Resize file tree"
          aria-valuemin={FILE_TREE_MIN_WIDTH}
          aria-valuemax={FILE_TREE_MAX_WIDTH}
          aria-valuenow={Math.round(width)}
          formatValueText={value => `${Math.round(value)} pixels`}
          onDragStart={onResizeStart}
          onDrag={onResize}
          onDragEnd={onResizeEnd}
          onDoubleClick={onResizeReset}
        />
      </div>
    </div>
  )
}

interface DiffFileEntryProps {
  file: DiffData['files'][number]
  contentUnavailable?: boolean
  viewFormat: ViewFormat
  isCollapsed: boolean
  onToggleFileCollapsed: () => void
}

function DiffFileEntry({file, contentUnavailable, viewFormat, isCollapsed, onToggleFileCollapsed}: DiffFileEntryProps) {
  const diffLines = useMemo(() => {
    if (file.lines) {
      return file.lines.map(line => ({
        type: line.type.toUpperCase() as StaticDiffLine['type'],
        html: line.html,
        text: line.text,
        left: line.left,
        right: line.right,
      }))
    }
    return file.patch ? parseUnifiedDiff(file.patch) : []
  }, [file.lines, file.patch])

  return (
    <div data-file-path={file.path} id={file.path}>
      <DiffFileHeader
        isCollapsed={isCollapsed}
        onToggleFileCollapsed={onToggleFileCollapsed}
        path={file.path}
        newPath={file.path}
        patchStatus={mapStatus(file.status)}
        linesAdded={file.additions}
        linesDeleted={file.deletions}
        linesChanged={file.changes}
      />
      {!isCollapsed && (
        <div className={styles.diffContainer}>
          {diffLines.length > 0 ? (
            viewFormat === 'split' ? (
              <StaticSplitDiffPreview diffLines={diffLines} className={styles.diffPreview} />
            ) : (
              <StaticUnifiedDiffPreview diffLines={diffLines} tabSize={4} className={styles.diffPreview} />
            )
          ) : (
            <div className={styles.emptyState}>
              {contentUnavailable
                ? 'File contents are unavailable. Reconnect to the environment to load the diff.'
                : 'Binary file or no patch available.'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
